import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { ActivityAsyncAdapter } from './activity.adapter';

import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { ReferenceAdapter } from '../reference.adapter';
import { ValueAdapter } from './../value.adapter';
import _ from 'lodash';
import { linkedServiceReferenceWithoutParameterizationRemainer } from '../reference-remainer';
import { DeleteActivity } from 'app/model/pipeline/delete.activity';
import { Dataset } from 'app/model/dataset/dataset';
import { fillParameterValuesForResource, convertParametersToModel } from 'app/shared/utils/parameter-converter.util';
import { StagingSettings } from 'app/model/pipeline/staging-settings';
import { LinkedService } from 'app/model/linkedservice/linkedservice';
import {
    convertStoreSettingsToModel,
    convertStoreSettingsToResource
} from './copysource/store-read-settings/store-read-settings.util';
import { linkedServiceReferenceToResource } from 'app/shared/utils/linkedservice-parameter.util';
import { generateUUID } from 'app/shared/utils/uuid.util';
import { ModelConstants, Owned } from 'shell-models-lib';
import { ActivityConnectionContext, IDeleteActivityContext } from 'app/interfaces/copilot.pipeline.action.types';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { ModelService } from 'app/model/services/model.service';
import { createDatasetWithLinkedService } from 'app/authoring/component/activities/activity-inline-dataset/utils';
import { findLinkedServiceFromContext } from 'app/shared/utils/update-pipeline-skill.util';
import { DatasetRegistryService } from 'app/registries/dataset/dataset-registry.service';
import { LinkedServiceRegistryService } from 'app/registries/linked-service/linked-service-registry.service';
import { isLogicAppExpression } from 'app/shared/utils/expression.util';
import { IFrameService } from 'app/services/iframe.service';
import { getLinkedServiceTypeFromDatasetMetadataAndConnectionKind } from 'app/shared/utils/linkedservice-type.util';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(DeleteActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        linkedServiceName: linkedServiceReferenceWithoutParameterizationRemainer,
        policy: '*',
        dependsOn: '*',
        typeProperties: {
            dataset: '*',
            datasetSettings: '*',
            recursive: '*',
            enableLogging: '*',
            logStorageSettings: {
                linkedServiceName: '*',
                path: '*'
            },
            maxConcurrentConnections: '*',
            storeSettings: '*'
        }
    }
})
export class DeleteActivityAdapter extends ActivityAsyncAdapter<
    DeleteActivity,
    Model.DeleteActivity,
    IDeleteActivityContext
> {
    public model(): IModel {
        return Metamodel.modelFor(DeleteActivity);
    }

    public override async postToModel(activity: DeleteActivity): Promise<void> {
        await super.postToModel(activity);
        if (__IS_FABRIC_DPE__ && activity.dataset) {
            const datasetRegistry = await getServiceInstance<DatasetRegistryService>(
                this.context.contextData,
                'DatasetRegistryService'
            );
            const iframeService = await getServiceInstance<IFrameService>(
                this.context.contextData,
                'IFrameService'
            );
            activity.dataset.customLinkedServiceType = getLinkedServiceTypeFromDatasetMetadataAndConnectionKind(
                activity.dataset,
                activity.dataset.linkedService?.tridentConnectionKind,
                {
                    iframeService,
                    datasetRegistry
                }
            );
        }
    }

    public override async toFabricCopilotContext(activity: DeleteActivity): Promise<IDeleteActivityContext> {
        if (activity) {
            const activityContext: IDeleteActivityContext = await super.toFabricCopilotContext(activity);
            if (activity.dataset) {
                const dsAdapter = this.context.asyncAdapterFor<Dataset, Model.Dataset, ActivityConnectionContext>(
                    Metamodel.modelOf(activity.dataset)
                );
                if (dsAdapter) {
                    activityContext.Connection = await dsAdapter.toFabricCopilotContext(activity.dataset);
                    activityContext.Recursive = ValueAdapter.toResource(
                        activity,
                        DeleteActivity.RECURSIVE,
                        true
                    )?.toString();
                }
            }
            return activityContext;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: IDeleteActivityContext,
        model?: DeleteActivity
    ): Promise<DeleteActivity> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            if (context.Connection) {
                const modelService = await getServiceInstance<ModelService>(this.context.contextData, 'ModelService');
                const iframeService = await getServiceInstance<IFrameService>(
                    this.context.contextData,
                    'IFrameService'
                );
                const linkedServiceInfo = await findLinkedServiceFromContext(context.Connection, {
                    modelService,
                    iframeService
                });
                if (linkedServiceInfo) {
                    const datasetRegistry = await getServiceInstance<DatasetRegistryService>(
                        this.context.contextData,
                        'DatasetRegistryService'
                    );
                    const linkedServiceRegistry = await getServiceInstance<LinkedServiceRegistryService>(
                        this.context.contextData,
                        'LinkedServiceRegistryService'
                    );
                    const { dataset } = createDatasetWithLinkedService(
                        linkedServiceInfo.linkedService,
                        linkedServiceInfo.linkedServiceType,
                        model,
                        { linkedServiceRegistry, datasetRegistry },
                        {
                            rootFolder: 'Files',
                            supportedFileFormats: [ModelConstants.binaryDataset]
                        }
                    );
                    if (dataset) {
                        dataset.customLinkedServiceType = linkedServiceInfo.linkedServiceType;
                        model.dataset = dataset;
                        const sourceDatasetAdapter = this.context.asyncAdapterFor<
                            Dataset,
                            Model.Dataset,
                            ActivityConnectionContext
                        >(Metamodel.modelOf(dataset));
                        model.dataset = await sourceDatasetAdapter.fromFabricCopilotContext(
                            context.Connection,
                            model.dataset
                        );
                    }
                }
            }
            if (context.Recursive != null) {
                let recursiveValue: string | boolean = context.Recursive;
                if (!isLogicAppExpression(recursiveValue)) {
                    recursiveValue = recursiveValue?.toLowerCase() === 'true' ? true : false;
                }
                ValueAdapter.toModel(model, DeleteActivity.RECURSIVE, recursiveValue);
            }
        }
        return model;
    }

    protected async _toResource(activity: DeleteActivity): Promise<Model.DeleteActivity> {
        const modelDeleteActivity: Model.DeleteActivity = super._getCommonActivityProperties(
            activity,
            'Delete'
        ) as Model.DeleteActivity;
        modelDeleteActivity.typeProperties = modelDeleteActivity.typeProperties || { dataset: undefined };

        if (activity.dataset) {
            if (this.context.adapterConfig.inlineDataset || this.context.adapterConfig.convertToFabric) {
                const datasetModel = Metamodel.modelOf(activity.dataset);
                const datasetAdapter = this.context.asyncAdapterFor(datasetModel);
                if (datasetAdapter) {
                    modelDeleteActivity.typeProperties.datasetSettings = (
                        await datasetAdapter.toResource(activity.dataset)
                    ).properties;
                } else {
                    this._logError('Cannot find dataset adapter in DeleteActivityAdapter', {
                        datasetName: activity.dataset.name,
                        datasetType: datasetModel?.name,
                    });
                }
            } else {
                modelDeleteActivity.typeProperties.dataset = this.getDatasetReference(activity);
            }
        }

        let logStorageSettings;
        if (
            (activity.enableLogging || DeleteActivity.ENABLE_LOGGING.templateValue(activity)) &&
            activity.logStorageSettings.storageLinkedService
        ) {
            logStorageSettings = {
                linkedServiceName: linkedServiceReferenceToResource(
                    activity.logStorageSettings.storageLinkedService,
                    this.context,
                    this.ensureLogger(),
                    activity.logStorageSettings.linkedServiceParameters
                ),
                path: ValueAdapter.toResource(activity.logStorageSettings, StagingSettings.PATH)
            };
        }
        modelDeleteActivity.typeProperties.logStorageSettings = logStorageSettings;
        modelDeleteActivity.typeProperties.enableLogging = ValueAdapter.toResource(
            activity,
            DeleteActivity.ENABLE_LOGGING
        );
        if (activity.storeSettings) {
            await convertStoreSettingsToResource(
                activity.storeSettings,
                modelDeleteActivity.typeProperties,
                this.context
            );
        } else {
            modelDeleteActivity.typeProperties.recursive = ValueAdapter.toResource(activity, DeleteActivity.RECURSIVE);
            modelDeleteActivity.typeProperties.maxConcurrentConnections = ValueAdapter.toResource(
                activity,
                DeleteActivity.MAXCONCURRENT_CONNECTIONS
            );
        }
        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            _.merge(modelDeleteActivity, parsed);
        }

        return modelDeleteActivity;
    }

    protected async _toModel(modelActivity: Model.DeleteActivity): Promise<DeleteActivity> {
        const activity: DeleteActivity = DeleteActivity.MODEL.create<DeleteActivity>(modelActivity.name);
        this._updatedCommonProperties(modelActivity, activity);
        modelActivity.typeProperties = modelActivity.typeProperties || { dataset: undefined };

        if (modelActivity.typeProperties.dataset) {
            activity.dataset = ReferenceAdapter.toProxyModel<Dataset>(
                Metamodel.modelFor(Dataset),
                modelActivity.typeProperties.dataset,
                this.context
            );
            if (modelActivity.typeProperties.dataset.parameters) {
                convertParametersToModel(
                    activity,
                    !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                    activity.dataset,
                    activity.datasetParameters,
                    modelActivity.typeProperties.dataset.parameters
                );
            }
        } else if (modelActivity.typeProperties.datasetSettings) {
            const datasetSettings = modelActivity.typeProperties.datasetSettings;
            const datasetModel = Metamodel.modelByName(datasetSettings.type);
            const datasetAdapter = this.context.asyncAdapterFor(datasetModel);
            const dataset = Owned.create<Dataset>(datasetModel, activity);
            await datasetAdapter.toModel(
                { name: generateUUID().replace(/-/g, '_'), properties: datasetSettings },
                dataset
            );
            activity.dataset = dataset;

            if (this._shouldGetArtifactDMTSConnectionFromSolutionTemplate()) {
                const dmtsCredentialKey = `${modelActivity.name}/Delete`;
                const dmtsCredentialForArtifact = this.context.adapterConfig.artifactDMTSCredentials.get(dmtsCredentialKey);
                if (dmtsCredentialForArtifact != null) {
                    activity.dataset.dmtsCredentialForArtifact = dmtsCredentialForArtifact;
                }
            }
        }

        if (modelActivity.typeProperties.storeSettings) {
            await convertStoreSettingsToModel(modelActivity.typeProperties.storeSettings, activity, this.context);
            try {
                // log http store settings usage in delete activity
                if (modelActivity.typeProperties.storeSettings.type === 'HttpReadSettings') {
                    this._logWarning('Unexpected http store settings usage in Delete activity', {
                        datasetType: activity.dataset != null ? Metamodel.modelOf(activity.dataset).name : undefined
                    });
                }
            } catch (error) {
                this._logError('Hit error when detect http store settings usage in Delete activity');
            }
        } else {
            if (modelActivity.typeProperties.recursive) {
                ValueAdapter.toModel(activity, DeleteActivity.RECURSIVE, modelActivity.typeProperties.recursive);
            }
            if (modelActivity.typeProperties.maxConcurrentConnections) {
                ValueAdapter.toModel(
                    activity,
                    DeleteActivity.MAXCONCURRENT_CONNECTIONS,
                    modelActivity.typeProperties.maxConcurrentConnections
                );
            }
        }

        ValueAdapter.toModel(activity, DeleteActivity.ENABLE_LOGGING, modelActivity.typeProperties.enableLogging);
        if (modelActivity.typeProperties.enableLogging) {
            if (modelActivity.typeProperties.logStorageSettings) {
                const logStorageSettings = modelActivity.typeProperties.logStorageSettings;
                activity.logStorageSettings.storageLinkedService = ReferenceAdapter.toProxyModel<LinkedService>(
                    Metamodel.modelFor(LinkedService),
                    logStorageSettings.linkedServiceName,
                    this.context
                );
                if (logStorageSettings.linkedServiceName && logStorageSettings.linkedServiceName.parameters) {
                    convertParametersToModel(
                        activity.logStorageSettings,
                        !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                        activity.logStorageSettings.storageLinkedService,
                        activity.logStorageSettings.linkedServiceParameters,
                        logStorageSettings.linkedServiceName.parameters
                    );
                }
                ValueAdapter.toModel(
                    activity.logStorageSettings,
                    StagingSettings.PATH,
                    modelActivity.typeProperties.logStorageSettings.path
                );
            }
        }

        activity.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelActivity, DeleteActivity.MODEL));
        return activity;
    }

    private getDatasetReference(activity: DeleteActivity): Model.DatasetReference {
        const datasetReference = ReferenceAdapter.toResource(
            activity.dataset as Dataset,
            this.context
        ) as Model.DatasetReference;
        datasetReference.parameters = fillParameterValuesForResource(
            activity.datasetParameters,
            this.context,
            this.ensureLogger()
        );
        return datasetReference;
    }
}
